import React from "react";
import { useLocation, useHistory } from "react-router-dom";
import { Paper, BottomNavigation, BottomNavigationAction } from "@mui/material";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import HistoryIcon from "@mui/icons-material/History";
import { KEYBOARD_OPEN_CLASS } from "../../utils/softKeyboard";

const TAB_ROUTES = ["/", "/flashcards", "/bookmarks", "/history"];

const selectedBg = "rgba(21,53,189,0.1)";
const selectedColor = "#1535BD";
const unselectedColor = "#8e97b0";

const tabSx = {
  color: unselectedColor,
  minWidth: 0,
  borderRadius: "12px",
  mx: 0.5,
  // No side padding, so the label below can use the whole tab (the icon is centred anyway).
  px: 0,
  transition: "background-color 0.2s",
  WebkitTapHighlightColor: "transparent",
  "& .MuiSvgIcon-root": {
    // Fixed px (MUI's default is em-based) so a large system font size can't grow the
    // icons and push the labels out of the 64px bar.
    width: 24,
    height: 24,
    transition: "transform 0.15s ease",
  },
  "&.Mui-selected .MuiSvgIcon-root": {
    transform: "scale(1.2)",
  },
  "& .MuiBottomNavigationAction-label": {
    fontSize: "0.6rem",
    fontWeight: 600,
    textTransform: "uppercase",
    opacity: 1,
    mt: 0.5,
    // Truncate rather than run into the next tab at large system font sizes. A label may
    // spill 2px into the 8px gap on either side, so one that only just overflows its tab
    // (the bold selected FLASHCARDS at 1.3x on a 360px phone) still shows in full.
    maxWidth: "calc(100% + 4px)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  "&.Mui-selected": {
    color: selectedColor,
    backgroundColor: selectedBg,
    "& .MuiBottomNavigationAction-label": {
      fontSize: "0.6rem",
      fontWeight: 800,
    },
  },
};

const BottomNav = React.memo(() => {
  const location = useLocation();
  const history = useHistory();

  const current = TAB_ROUTES.includes(location.pathname) ? location.pathname : false;

  return (
    <Paper
      sx={{
        display: { xs: "block", sm: "none" },
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        pb: "env(safe-area-inset-bottom, 0px)",
        borderRadius: "16px 16px 0 0",
        border: "1.5px solid rgba(21,53,189,0.12)",
        borderBottom: "none",
        overflow: "hidden",
        // Native tab bars give way to the keyboard; this one would ride on top of it.
        [`body.${KEYBOARD_OPEN_CLASS} &`]: { display: "none" },
      }}
      elevation={4}
    >
      <BottomNavigation
        value={current}
        onChange={(_, newValue) => history.push(newValue)}
        sx={{ backgroundColor: "white", height: 64, px: 1, alignItems: "center" }}
      >
        <BottomNavigationAction label="Home"       value="/"           icon={<HomeOutlinedIcon />}    sx={tabSx} />
        <BottomNavigationAction label="Flashcards" value="/flashcards" icon={<StyleOutlinedIcon />}   sx={tabSx} />
        <BottomNavigationAction label="Bookmarks"  value="/bookmarks"  icon={<BookmarkBorderIcon />}  sx={tabSx} />
        <BottomNavigationAction label="History"    value="/history"    icon={<HistoryIcon />}         sx={tabSx} />
      </BottomNavigation>
    </Paper>
  );
});

export default BottomNav;
